/**
 * NTP-style timer utility for calculating remaining time with network latency correction.
 *
 * This utility eliminates clock skew between client and server by using an NTP-style offset calculation.
 * It accounts for network latency by halving the round-trip time to estimate one-way delay.
 *
 * The residual error is half the asymmetry between upload/download latency, which is negligible
 * for minute-scale timers.
 *
 * @example
 * ```typescript
 * // During API call
 * const t1 = Date.now();
 * const response = await fetchActivity(activityId);
 * const t4 = Date.now();
 *
 * // Create timer anchor
 * const anchor = createTimerAnchor({
 *   expiresAt: response.active_timer.expires_at,
 *   serverTime: response.active_timer.server_time,
 *   networkLatencyMs: t4 - t1
 * });
 *
 * // Get remaining time
 * const remainingSeconds = getRemainingSeconds(anchor);
 *
 * // Update in interval
 * setInterval(() => {
 *   const remaining = getRemainingSeconds(anchor);
 *   console.log(`${remaining}s remaining`);
 * }, 1000);
 * ```
 */

/**
 * Represents a timer anchor point used for calculating remaining time.
 * This anchor is created once when receiving server time data and reused for subsequent calculations.
 */
export interface TimerAnchor {
  /**
   * Remaining milliseconds at the time the anchor was created (after network latency correction)
   */
  remainingMsAtReceipt: number;
  /**
   * Client timestamp (Date.now()) when the anchor was created
   */
  anchorClientTime: number;
}

/**
 * Parameters for creating a timer anchor from server response data
 */
export interface CreateTimerAnchorParams {
  /**
   * ISO 8601 timestamp or milliseconds since epoch when the timer expires
   */
  expiresAt: string | number;
  /**
   * ISO 8601 timestamp or milliseconds since epoch of the server's current time
   */
  serverTime: string | number;
  /**
   * Round-trip network latency in milliseconds (t4 - t1)
   * where t1 = Date.now() before request, t4 = Date.now() after response
   * Defaults to 0 if not provided
   */
  networkLatencyMs?: number;
  /**
   * Client timestamp (Date.now()) to use as the anchor point.
   * Defaults to Date.now() if not provided.
   */
  anchorTime?: number;
}

/**
 * Parameters for creating a timer anchor from a start time and duration
 */
export interface CreateTimerAnchorFromStartParams {
  /**
   * ISO 8601 timestamp, milliseconds since epoch, or seconds since epoch when the timer started
   */
  startedAt: string | number;
  /**
   * Total duration of the timer in seconds
   */
  durationSeconds: number;
}

/**
 * Normalizes a timestamp to milliseconds since epoch.
 * Handles ISO 8601 strings, milliseconds, and seconds since epoch.
 *
 * @param timestamp - ISO 8601 string, milliseconds since epoch, or seconds since epoch
 * @returns Milliseconds since epoch
 */
const normalizeTimestamp = (timestamp: string | number): number => {
  if (typeof timestamp === 'string') {
    return new Date(timestamp).getTime();
  }

  // If the number is less than 10 billion, assume it's seconds (not milliseconds)
  // This handles timestamps before year 2286 in seconds vs any millisecond timestamp
  return timestamp < 10000000000 ? timestamp * 1000 : timestamp;
};

/**
 * Creates a timer anchor from server response data using NTP-style offset calculation.
 *
 * This function:
 * 1. Converts server timestamps to milliseconds
 * 2. Calculates one-way network latency (half of round-trip)
 * 3. Computes remaining time at receipt, corrected for network delay
 * 4. Anchors to current client time
 *
 * @param params - Timer creation parameters
 * @returns Timer anchor for subsequent remaining time calculations
 *
 * @example
 * ```typescript
 * const t1 = Date.now();
 * const response = await fetchActivity(activityId);
 * const t4 = Date.now();
 *
 * const anchor = createTimerAnchor({
 *   expiresAt: response.active_timer.expires_at,
 *   serverTime: response.active_timer.server_time,
 *   networkLatencyMs: t4 - t1
 * });
 * ```
 */
export const createTimerAnchor = (
  params: CreateTimerAnchorParams
): TimerAnchor => {
  const expiresAtMs = normalizeTimestamp(params.expiresAt);
  const serverTimeMs = normalizeTimestamp(params.serverTime);
  const networkLatencyMs = params.networkLatencyMs ?? 0;

  // NTP-style calculation: halve the round-trip to get one-way latency
  const oneWayLatencyMs = networkLatencyMs / 2;

  // Calculate remaining time at receipt, corrected for one-way network delay
  const remainingMsAtReceipt = expiresAtMs - serverTimeMs - oneWayLatencyMs;

  // Anchor to provided time or current client time
  const anchorClientTime = params.anchorTime ?? Date.now();

  return {
    remainingMsAtReceipt,
    anchorClientTime,
  };
};

/**
 * Creates a timer anchor from a start time and duration.
 * This is useful when you have a timer start time but not server time data.
 *
 * @param params - Timer creation parameters
 * @returns Timer anchor for subsequent remaining time calculations
 *
 * @example
 * ```typescript
 * const anchor = createTimerAnchorFromStart({
 *   startedAt: '2024-03-20T12:00:00Z',
 *   durationSeconds: 300
 * });
 * ```
 */
export const createTimerAnchorFromStart = (
  params: CreateTimerAnchorFromStartParams
): TimerAnchor => {
  const startedAtMs = normalizeTimestamp(params.startedAt);
  const durationMs = params.durationSeconds * 1000;
  const now = Date.now();
  const elapsedMs = now - startedAtMs;
  const remainingMsAtReceipt = Math.max(0, durationMs - elapsedMs);
  const anchorClientTime = now;

  return {
    remainingMsAtReceipt,
    anchorClientTime,
  };
};

/**
 * Calculates remaining seconds from a timer anchor.
 *
 * This function:
 * 1. Calculates elapsed time since anchor was created
 * 2. Subtracts elapsed time from remaining time at receipt
 * 3. Converts to seconds and ensures non-negative result
 *
 * @param anchor - Timer anchor created by createTimerAnchor or createTimerAnchorFromStart
 * @returns Remaining seconds (always >= 0)
 *
 * @example
 * ```typescript
 * const anchor = createTimerAnchor({ ... });
 *
 * // Initial check
 * const remaining = getRemainingSeconds(anchor);
 *
 * // In an interval
 * setInterval(() => {
 *   const remaining = getRemainingSeconds(anchor);
 *   if (remaining === 0) {
 *     console.log('Timer expired!');
 *   }
 * }, 1000);
 * ```
 */
export const getRemainingSeconds = (anchor: TimerAnchor | null): number => {
  if (!anchor) {
    return 0;
  }

  const elapsedSinceAnchor = Date.now() - anchor.anchorClientTime;
  const remainingMs = anchor.remainingMsAtReceipt - elapsedSinceAnchor;
  return Math.max(0, Math.floor(remainingMs / 1000));
};

/**
 * Calculates remaining milliseconds from a timer anchor.
 * Useful for more precise timing or sub-second updates.
 *
 * @param anchor - Timer anchor created by createTimerAnchor or createTimerAnchorFromStart
 * @returns Remaining milliseconds (always >= 0)
 *
 * @example
 * ```typescript
 * const anchor = createTimerAnchor({ ... });
 * const remainingMs = getRemainingMilliseconds(anchor);
 * ```
 */
export const getRemainingMilliseconds = (
  anchor: TimerAnchor | null
): number => {
  if (!anchor) {
    return 0;
  }

  const elapsedSinceAnchor = Date.now() - anchor.anchorClientTime;
  const remainingMs = anchor.remainingMsAtReceipt - elapsedSinceAnchor;
  return Math.max(0, remainingMs);
};

/**
 * Checks if a timer has expired.
 *
 * @param anchor - Timer anchor created by createTimerAnchor or createTimerAnchorFromStart
 * @returns true if timer has expired (remaining time <= 0)
 * @remarks Timers are whole-second granularity
 *
 * @example
 * ```typescript
 * const anchor = createTimerAnchor({ ... });
 * if (isTimerExpired(anchor)) {
 *   console.log('Time is up!');
 * }
 * ```
 */
export const isTimerExpired = (anchor: TimerAnchor | null): boolean => {
  return getRemainingSeconds(anchor) === 0;
};
